import { axiosPost } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import { convertQueryParamsForPostRequest } from '@/api/utils';
import { BulkResponseModel } from '@/features/batch/_models/batch';
import { invalidateBatchListMap } from '@/features/batch/_utils/query-keys';
import type {
  ReportInvestmentFundsFilterSchema,
  ReportLocalGovernmentFundsFilterSchema,
  ReportOrganizingTableFilterSchema,
  ReportStandardFundsFilterSchema,
  reportFileType,
} from '@/features/mold/models';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';

type FileTypeArg = {
  fileType: z.infer<typeof reportFileType>;
};
type AllOrganizationArg = {
  isAllOrganization: boolean;
};

export const useGenerateOrganizingTableReport = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      params: z.infer<typeof ReportOrganizingTableFilterSchema> & FileTypeArg,
    ) => {
      const { formatNumber, fileType, ...payload } = params;
      const query = convertQueryParamsForPostRequest({
        ...payload,
        doRound: fileType === 'pdf',
      });
      const res = await axiosPost(
        `/api/project-reports/${formatNumber}/?${query}`,
        {},
      );
      await Promise.all([
        invalidateBatchListMap.batchList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      ]);
      return BulkResponseModel.parse(res);
    },
  });
};

export const useGenerateStandardFundsReport = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ReportStandardFundsFilterSchema> &
        FileTypeArg &
        AllOrganizationArg,
    ) => {
      const query = convertQueryParamsForPostRequest({
        ...values,
        doRound: values.fileType === 'pdf',
      });
      const res = await axiosPost(
        `/api/project-reports/standard-fund-report/?${query}`,
        {},
      );
      await Promise.all([
        invalidateBatchListMap.batchList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      ]);
      return BulkResponseModel.parse(res);
    },
  });
};

export const useGenerateInvestmentFundsReport = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ReportInvestmentFundsFilterSchema> &
        FileTypeArg &
        AllOrganizationArg,
    ) => {
      const query = convertQueryParamsForPostRequest({
        ...values,
        doRound: values.fileType === 'pdf',
      });
      const res = await axiosPost(
        `/api/project-reports/investment-fund-report/?${query}`,
        {},
      );
      await Promise.all([
        invalidateBatchListMap.batchList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      ]);
      return BulkResponseModel.parse(res);
    },
  });
};

export const useGenerateLocalGovernmentFundsReport = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ReportLocalGovernmentFundsFilterSchema> &
        FileTypeArg &
        AllOrganizationArg,
    ) => {
      const query = convertQueryParamsForPostRequest({
        ...values,
        doRound: values.fileType === 'pdf',
      });
      const res = await axiosPost(
        `/api/project-reports/local-government-fund-report/?${query}`,
        {},
      );
      await Promise.all([
        invalidateBatchListMap.batchList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      ]);
      return BulkResponseModel.parse(res);
    },
  });
};
